import React, { useRef, useState, useLayoutEffect, useEffect } from 'react'
import { usePinch, useDrag } from '@use-gesture/react'

const clamp = (value, min, max) => (value < min ? min : value > max ? max : value)

function PinchableWrap({ children, maxScale = 4 }) {
  const containerRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const [maxBounds, setMaxBounds] = useState({ x: 0, y: 0 })

  useLayoutEffect(() => {
    const host = containerRef.current
    if (!host) return
    const content = host.firstElementChild
    if (!content) return

    const rect = content.getBoundingClientRect()
    const maxX = Math.floor((rect.width * (scale - 1)) / 2 || 0)
    const maxY = Math.floor((rect.height * (scale - 1)) / 2 || 0)
    setMaxBounds({ x: maxX, y: maxY })

    if (scale === 1) {
      setTranslateX(0)
      setTranslateY(0)
    } else {
      setTranslateX((prev) => clamp(prev, -maxX, maxX))
      setTranslateY((prev) => clamp(prev, -maxY, maxY))
    }
  }, [scale])

  // Prevent Safari accessibility zoom conflicts for Webkit GestureEvents
  useEffect(() => {
    const prevent = (e) => e.preventDefault()
    document.addEventListener('gesturestart', prevent)
    document.addEventListener('gesturechange', prevent)
    return () => {
      document.removeEventListener('gesturestart', prevent)
      document.removeEventListener('gesturechange', prevent)
    }
  }, [])

  usePinch(
    ({ offset: [s], first, memo = scale }) => {
      const next = clamp(first ? memo : s, 1, maxScale)
      setScale(next)
      return memo
    },
    {
      target: containerRef,
      from: () => [scale, 0],
      scaleBounds: { min: 1, max: maxScale },
      rubberband: false,
      eventOptions: { passive: false },
    }
  )

  useDrag(
    ({ offset: [dx, dy] }) => {
      if (scale === 1) return
      setTranslateX(clamp(dx, -maxBounds.x, maxBounds.x))
      setTranslateY(clamp(dy, -maxBounds.y, maxBounds.y))
    },
    {
      target: containerRef,
      from: () => [translateX, translateY],
      pointer: { touch: true },
      filterTaps: true,
    }
  )

  return (
    <div
      ref={containerRef}
      style={{
        touchAction: scale > 1 ? 'none' : 'auto',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        WebkitUserDrag: 'none',
        userSelect: 'none',
        display: 'contents',
      }}
    >
      <div
        style={{
          transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
          transformOrigin: 'center center',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default PinchableWrap
